import { useEffect, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, Check, Loader2 } from "lucide-react";
import { api } from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatCurrency, formatDate, formatDateTime, formatNumber, formatUnit } from "@/lib/format";
import type { components } from "@dolfin/api-admin";

type PurchaseOrder = components["schemas"]["PurchaseOrderResponse"];

// Reads the admin by-id endpoint: staff hold only a PO id, and the tenant one needs an active organisation.
export function PurchaseOrderPage() {
  const { purchaseOrderId } = useParams<{ purchaseOrderId: string }>();
  const [po, setPo] = useState<PurchaseOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!purchaseOrderId) return;
    let cancelled = false;
    setLoading(true);
    api
      .GET("/admin/purchase-orders/{id}", { params: { path: { id: purchaseOrderId } } })
      .then(({ data, error: apiError }) => {
        if (cancelled) return;
        if (apiError || !data) {
          setError("Purchase order not found.");
          return;
        }
        setPo(data);
      })
      .catch(() => !cancelled && setError("Purchase order could not be loaded."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [purchaseOrderId]);

  if (loading) {
    return (
      <div className="text-muted-foreground flex items-center justify-center gap-2 py-16 text-sm">
        <Loader2 className="size-4 animate-spin" /> Loading purchase order…
      </div>
    );
  }

  if (error || !po) {
    return (
      <div className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-3 rounded-lg border p-4 text-sm">
        <AlertTriangle className="mt-0.5 size-4" />
        <span>{error ?? "Purchase order not found."}</span>
      </div>
    );
  }

  const lines = po.lines ?? [];
  const currency = po.currency ?? "GBP";
  const net = lines.reduce((sum, l) => sum + num(l.subTotal), 0);
  const tax = lines.reduce((sum, l) => sum + num(l.taxAmount), 0);

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-mono text-2xl font-bold">PO {po.poNumber}</h1>
          <p className="text-muted-foreground mt-1 text-xs">{po.id}</p>
        </div>
        <div className="flex items-center gap-2">
          {po.status && <Badge variant="outline">{String(po.status)}</Badge>}
          {po.organisationId && (
            <Link
              to={`/organisations/${po.organisationId}#purchase-orders`}
              className="text-muted-foreground text-sm hover:underline"
            >
              Organisation
            </Link>
          )}
        </div>
      </div>

      <dl className="grid [&>div]:min-w-0 grid-cols-2 gap-4 text-sm md:grid-cols-4">
        <Field label="Currency" value={po.currency} mono />
        <Field label="External ref" value={po.externalRef ?? null} mono />
        <Field
          label="Supplier"
          value={
            po.supplierId ? (
              <Link to={`/suppliers/${po.supplierId}`} className="font-mono text-xs hover:underline">
                {po.supplierId}
              </Link>
            ) : null
          }
        />
        <Field label="Order date" value={po.orderDate ? formatDate(po.orderDate) : null} />
        <Field
          label="Expected delivery"
          value={po.expectedDeliveryDate ? formatDate(po.expectedDeliveryDate) : null}
        />
        <Field label="Lines" value={String(lines.length)} />
        <Field label="Created" value={po.createdAt ? formatDateTime(po.createdAt) : null} />
        <Field label="Last modified" value={po.updatedAt ? formatDateTime(po.updatedAt) : null} />
      </dl>

      <div className="border-border rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/40">
              {/* The order the integrator sent, never renumbered - staff compare it against the source doc. */}
              <TableHead className="w-10 text-right">#</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>SKU</TableHead>
              <TableHead>External ref</TableHead>
              <TableHead className="text-right">Ordered</TableHead>
              <TableHead>Unit</TableHead>
              <TableHead className="text-right">Matched</TableHead>
              <TableHead className="text-right">Unit price</TableHead>
              <TableHead className="text-right">Net</TableHead>
              <TableHead className="text-right">Tax rate</TableHead>
              <TableHead className="text-right">Tax</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {lines.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} className="text-muted-foreground text-center">
                  No lines on this order.
                </TableCell>
              </TableRow>
            ) : (
              lines.map((l) => (
                <TableRow key={l.id}>
                  <TableCell className="text-muted-foreground text-right tabular-nums">
                    {l.sortOrder ?? "-"}
                  </TableCell>
                  <TableCell>{l.description}</TableCell>
                  <TableCell className="font-mono text-xs">{l.sku ?? "-"}</TableCell>
                  <TableCell className="text-muted-foreground font-mono text-xs">
                    {l.externalRef ?? "-"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{formatNumber(l.quantityOrdered)}</TableCell>
                  {/* Structured unitOfMeasure wins over free text: catch-weight matching keys on it. */}
                  <TableCell className="text-muted-foreground">
                    {formatUnit(l.unit, l.unitOfMeasure) ?? "-"}
                  </TableCell>
                  {/* Remaining capacity decides whether a bill line can still match here. */}
                  <TableCell className="text-right tabular-nums">
                    <MatchedQuantity matched={l.matchedQuantity} fullyMatched={l.fullyMatched} />
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatCurrency(l.unitPrice, currency)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatCurrency(l.subTotal, currency)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{formatNumber(l.taxRate)}%</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatCurrency(l.taxAmount, currency)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {lines.length > 0 && (
        <dl className="ml-auto grid w-64 grid-cols-2 gap-x-6 gap-y-1 text-sm">
          <dt className="text-muted-foreground">Net</dt>
          <dd className="text-right tabular-nums">{formatCurrency(net, currency)}</dd>
          <dt className="text-muted-foreground">Tax</dt>
          <dd className="text-right tabular-nums">{formatCurrency(tax, currency)}</dd>
          <dt className="font-medium">Gross</dt>
          <dd className="text-right font-medium tabular-nums">{formatCurrency(net + tax, currency)}</dd>
        </dl>
      )}
    </div>
  );
}

function num(value: number | string | null | undefined): number {
  const n = typeof value === "string" ? parseFloat(value) : (value ?? 0);
  return Number.isFinite(n) ? n : 0;
}

// Null matchedQuantity means "not computed", not zero: don't render a 0 that reads as fact.
function MatchedQuantity({
  matched,
  fullyMatched,
}: {
  matched: number | string | null | undefined;
  fullyMatched: boolean | null | undefined;
}) {
  if (matched == null) return <span className="text-muted-foreground">-</span>;
  return (
    <span className="inline-flex items-center justify-end gap-1">
      {formatNumber(matched)}
      {fullyMatched && <Check className="size-3.5 text-emerald-600 dark:text-emerald-400" />}
    </span>
  );
}

function Field({ label, value, mono = false }: { label: string; value: ReactNode; mono?: boolean }) {
  return (
    <div>
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd className={`mt-0.5 ${mono ? "font-mono text-xs" : ""}`}>
        {value ?? <span className="text-muted-foreground">-</span>}
      </dd>
    </div>
  );
}
